/**
 * API Error Tracking with Sentry
 *
 * Specialized utilities for tracking API errors with detailed context
 */

import type { AxiosError } from "axios";
import {
	addBreadcrumb,
	captureException,
	setContext,
	setTag,
} from "./error-tracking";

/**
 * Custom API Error class with enhanced context
 */
export class ApiError extends Error {
	public readonly statusCode?: number;
	public readonly endpoint: string;
	public readonly method: string;
	public readonly requestData?: unknown;
	public readonly responseData?: unknown;
	public readonly isNetworkError: boolean;
	public readonly isTimeout: boolean;

	constructor(message: string, axiosError: AxiosError) {
		super(message);
		this.name = "ApiError";

		// Extract useful information from axios error
		this.statusCode = axiosError.response?.status;
		this.endpoint = axiosError.config?.url || "unknown";
		this.method = (axiosError.config?.method || "unknown").toUpperCase();
		this.requestData = axiosError.config?.data;
		this.responseData = axiosError.response?.data;
		this.isNetworkError = !axiosError.response;
		this.isTimeout = axiosError.code === "ECONNABORTED";
	}
}

/**
 * Options for tracking API errors
 */
export interface TrackApiErrorOptions {
	/** Additional tags to add to the error */
	tags?: Record<string, string>;

	/** Additional context to add to the error */
	extra?: Record<string, unknown>;

	/** Whether to log the error to console (default: true in dev) */
	logToConsole?: boolean;

	/** Custom error message override */
	customMessage?: string;

	/** Whether to include request/response data (default: false for security) */
	includeRequestData?: boolean;
	includeResponseData?: boolean;

	/** Severity level */
	level?: "fatal" | "error" | "warning" | "info";
}

/**
 * Track an API error with Sentry
 *
 * @example
 * catch (error) {
 *   trackApiError(error, {
 *     tags: { service: 'quote', feature: 'calculator' },
 *     level: 'error'
 *   });
 * }
 */
export function trackApiError(
	error: unknown,
	options: TrackApiErrorOptions = {},
): void {
	// Only track if it's an Axios error
	if (!isAxiosError(error)) {
		return;
	}

	const {
		tags = {},
		extra = {},
		logToConsole = import.meta.env.DEV,
		customMessage,
		includeRequestData = false,
		includeResponseData = false,
		level = "error",
	} = options;

	// Extract error details
	const statusCode = error.response?.status;
	const endpoint = error.config?.url || "unknown";
	const method = (error.config?.method || "unknown").toUpperCase();
	const baseURL = error.config?.baseURL || "";

	// Create enhanced error
	const apiError = new ApiError(
		customMessage || error.message || "API request failed",
		error,
	);

	// Log to console in development
	if (logToConsole) {
		console.error("🔥 API Error:", {
			endpoint,
			method,
			statusCode,
			message: apiError.message,
			isNetworkError: apiError.isNetworkError,
			isTimeout: apiError.isTimeout,
		});
	}

	// Set tags for filtering in Sentry
	setTag("error_type", "api_error");
	setTag("api_endpoint", endpoint);
	setTag("api_method", method);

	if (statusCode) {
		setTag("api_status_code", statusCode.toString());
		setTag("api_status_category", getStatusCategory(statusCode));
	}

	// Set additional tags
	Object.entries(tags).forEach(([key, value]) => {
		setTag(key, value);
	});

	// Build context with API details
	const apiContext: Record<string, unknown> = {
		endpoint,
		method,
		statusCode,
		baseURL,
		fullURL: `${baseURL}${endpoint}`,
		isNetworkError: apiError.isNetworkError,
		isTimeout: apiError.isTimeout,
		...extra,
	};

	// Conditionally include request/response data
	if (includeRequestData && error.config?.data) {
		apiContext.requestData = sanitizeData(error.config.data);
	}

	if (includeResponseData && error.response?.data) {
		apiContext.responseData = sanitizeData(error.response.data);
	}

	// Add error response details if available
	if (error.response) {
		apiContext.responseHeaders = error.response.headers;
		apiContext.statusText = error.response.statusText;
	}

	setContext("api_error", apiContext);

	// Capture the error
	captureException(apiError, {
		level,
		tags: {
			error_type: "api_error",
			api_endpoint: endpoint,
			api_method: method,
			...(statusCode && { api_status_code: statusCode.toString() }),
			...tags,
		},
		extra: apiContext,
	});
}

/**
 * Add an API request breadcrumb
 * Use this to track API calls before they happen
 *
 * @example
 * addApiRequestBreadcrumb('POST', '/transactions/quote', { amount: 1000 });
 */
export function addApiRequestBreadcrumb(
	method: string,
	endpoint: string,
	data?: Record<string, unknown>,
): void {
	addBreadcrumb({
		category: "api.request",
		message: `${method.toUpperCase()} ${endpoint}`,
		level: "info",
		data: {
			method: method.toUpperCase(),
			endpoint,
			...(data && { requestData: sanitizeData(data) }),
		},
	});
}

/**
 * Add an API response breadcrumb
 * Use this to track successful API responses
 *
 * @example
 * addApiResponseBreadcrumb('POST', '/transactions/quote', 200);
 */
export function addApiResponseBreadcrumb(
	method: string,
	endpoint: string,
	statusCode: number,
	data?: Record<string, unknown>,
): void {
	addBreadcrumb({
		category: "api.response",
		message: `${method.toUpperCase()} ${endpoint} - ${statusCode}`,
		level: statusCode >= 400 ? "error" : "info",
		data: {
			method: method.toUpperCase(),
			endpoint,
			statusCode,
			...(data && { responseData: sanitizeData(data) }),
		},
	});
}

/**
 * Track specific API error types
 */
export function trackAuthenticationError(error: AxiosError): void {
	trackApiError(error, {
		tags: {
			error_category: "authentication",
			severity: "high",
		},
		level: "warning",
	});
}

export function trackAuthorizationError(error: AxiosError): void {
	trackApiError(error, {
		tags: {
			error_category: "authorization",
			severity: "medium",
		},
		level: "warning",
	});
}

export function trackServerError(error: AxiosError): void {
	trackApiError(error, {
		tags: {
			error_category: "server_error",
			severity: "critical",
		},
		level: "error",
		includeResponseData: true, // Include response for debugging server errors
	});
}

export function trackNetworkError(error: AxiosError): void {
	trackApiError(error, {
		tags: {
			error_category: "network",
			severity: "medium",
		},
		level: "warning",
	});
}

export function trackValidationError(error: AxiosError): void {
	trackApiError(error, {
		tags: {
			error_category: "validation",
			severity: "low",
		},
		level: "warning",
		includeResponseData: true, // Include response to see validation errors
	});
}

/**
 * Helper: Check if error is an Axios error
 */
export function isAxiosError(error: unknown): error is AxiosError {
	return (
		typeof error === "object" &&
		error !== null &&
		"isAxiosError" in error &&
		(error as AxiosError).isAxiosError === true
	);
}

/**
 * Helper: Get status code category
 */
function getStatusCategory(statusCode: number): string {
	if (statusCode >= 500) return "5xx_server_error";
	if (statusCode >= 400) return "4xx_client_error";
	if (statusCode >= 300) return "3xx_redirect";
	if (statusCode >= 200) return "2xx_success";
	return "unknown";
}

/**
 * Helper: Sanitize sensitive data before sending to Sentry
 */
function sanitizeData(data: unknown): unknown {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	// List of sensitive field names to redact
	const sensitiveFields = [
		"password",
		"token",
		"apiKey",
		"api_key",
		"secret",
		"authorization",
		"auth",
		"creditCard",
		"credit_card",
		"ssn",
		"socialSecurity",
		"social_security",
		"pin",
		"cvv",
		"cvc",
	];

	// Clone and sanitize
	const sanitized = JSON.parse(JSON.stringify(data));

	function redactSensitiveFields(obj: Record<string, unknown>): void {
		for (const key in obj) {
			const lowerKey = key.toLowerCase();

			// Check if the key is sensitive
			if (
				sensitiveFields.some((field) => lowerKey.includes(field.toLowerCase()))
			) {
				obj[key] = "[REDACTED]";
			} else if (typeof obj[key] === "object" && obj[key] !== null) {
				// Recursively sanitize nested objects
				redactSensitiveFields(obj[key] as Record<string, unknown>);
			}
		}
	}

	if (typeof sanitized === "object") {
		redactSensitiveFields(sanitized as Record<string, unknown>);
	}

	return sanitized;
}

/**
 * Helper: Check if error should be tracked based on status code
 */
export function shouldTrackError(statusCode?: number): boolean {
	if (!statusCode) return true; // Track network errors

	// Don't track these status codes (they're expected)
	const ignoredStatusCodes = [
		401, // Unauthorized - expected when token expires
		404, // Not Found - expected for missing resources
	];

	return !ignoredStatusCodes.includes(statusCode);
}

/**
 * Format error message for user display
 */
export function formatApiErrorMessage(error: unknown): string {
	if (!isAxiosError(error)) {
		return "An unexpected error occurred";
	}

	const statusCode = error.response?.status;

	// Custom messages based on status code
	if (statusCode === 401) {
		return "Your session has expired. Please refresh the page.";
	}

	if (statusCode === 403) {
		return "You do not have permission to perform this action.";
	}

	if (statusCode === 404) {
		return "The requested resource was not found.";
	}

	if (statusCode === 422) {
		return "Please check your input and try again.";
	}

	if (statusCode && statusCode >= 500) {
		return "A server error occurred. Please try again later.";
	}

	if (!error.response) {
		return "Unable to connect to the server. Please check your internet connection.";
	}

	// Try to extract message from response
	const responseData = error.response?.data as { message?: string };
	if (responseData?.message) {
		return responseData.message;
	}

	return "An error occurred. Please try again.";
}
